import React, { FC, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInput } from '@microservices/wiskey-react-components'
import { Divider, Grid, Typography, useTheme } from '@mui/material'

import { DropDownInputWithFitlers } from '@components/DropDownInputWithFitlers'
import { FilterDialog } from '@components/FilterDialog'

import { useFetchAllViewQuery, useFetchObjectByCodeQuery } from '@redux/api'

import { isObjectValueType } from '@helpers'
import {
  DROPDOWN_FILTER_TYPES,
  ENTITY_TYPES_REQUEST,
  FIELD_VALUE_TYPE,
  OBJECT_FIELD_TYPE,
  SEARCH_FIELD_TYPE,
} from '@constants'
import { GENERATOR_INPUT_TYPE } from '@constants'
import { GETCriteriaType, GETDropDownFilter, GETEntityFilter } from '@types'

import { ModalRefPathPicker } from '@gantt/components/GanttCreateOrEdit/components/ModalRefPathPicker'

import { SearchFilterValue } from '../../types'

import { useResetGroupRelatedCheckboxOnValueNameChange } from './hooks/useResetGroupRelatedCheckboxOnValueNameChange'
import {
  useResetAsCheckboxOnValueTypeChange,
  useResetValuesOnGroupRelatedCheckboxChange,
  useResetValuesOnObjectTitleChange,
  useResetValuesOnValueNameChange,
  useValueInputs,
  useValueTypeOfSelectedValue,
} from './hooks'

type ValueInputsProps = {
  index: number
  addedFiltersByObject: GETCriteriaType[]
}

export const ValueInputs: FC<ValueInputsProps> = ({ index, addedFiltersByObject }) => {
  const { t } = useTranslation()
  const theme = useTheme()

  const [isShowFilterDialog, setShowFilterDialog] = useState(false)
  const [currentFilter, setCurrentFilter] = useState<GETEntityFilter | null>(null)
  const [currentFilterPosition, setCurrentFilterPosition] = useState<number | null>(null)
  const [currentTypeFilter, setCurrentTypeFilter] = useState<DROPDOWN_FILTER_TYPES | null>(null)

  const {
    setValue,
    watch,
    formState: { isDirty },
  } = useFormContext()

  const valueName = `values.${index}`
  const typeName = `values.${index}.type`
  const valueNameInputName = `values.${index}.name`
  const valuePathArrayName = `values.${index}.valuePathArray`
  const valueTypeInputName = `values.${index}.valueType`
  const asCheckboxInputName = `values.${index}.asCheckbox`
  const asDurationInputName = `values.${index}.asDuration`
  const fieldTypeInputName = `values.${index}.fieldType`
  const formatDateInputName = `values.${index}.formatDate`
  const timeIncrementInputName = `values.${index}.timeIncrement`
  const objectValueInputName = `values.${index}.objectValue`
  const objectValuePathArrayName = `values.${index}.pathArray`
  const linkedObjectCodeInputName = `values.${index}.linkedObjectCode`
  const defaultPlaceholderInputName = `values.${index}.isDefaultPlaceholder`
  const groupRelatedInputName = `values.${index}.isGroupRelated`
  const placeholderValueInputName = `values.${index}.placeholderValue`
  const placeholderStartInputName = `values.${index}.placeholderStart`
  const placeholderEndInputName = `values.${index}.placeholderEnd`
  const dropDownListCodeInputName = `values.${index}.dropDownListCode`
  const dropDownWindowInputName = `values.${index}.dropDownWindowCode`
  const dropDownListFiltersInputName = `values.${index}.dropDownListFilters`
  const dropDownWindowFiltersInputName = `values.${index}.dropDownWindowFilters`
  const valueMinName = `values.${index}.min`
  const valueMaxName = `values.${index}.max`
  const timeUnitName = `values.${index}.timeUnit`
  const filterByObjectInputName = `values.${index}.isFilterByObject`

  const watchedValue = watch(valueName)
  const watchedValueName = watch(valueNameInputName)
  const watchedValuePathArray = watch(valuePathArrayName)
  const watchedValueType = watch(valueTypeInputName)
  const watchedType = watch(typeName)
  const watchedAsCheckbox = watch(asCheckboxInputName)
  const watchedAsDuration = watch(asDurationInputName)
  const watchedFieldType = watch(fieldTypeInputName)
  const watchedTimeIncrement = watch(timeIncrementInputName) // необходимо прокинуть
  const watchedObjectValue = watch(objectValueInputName)
  const watchedDropDownListCode = watch(dropDownListCodeInputName)
  const watchedDropDownWindowCode = watch(dropDownWindowInputName)
  const watchedDefaultplaceholder = watch(defaultPlaceholderInputName)
  const watchedGroupRelated = watch(groupRelatedInputName)
  const watchedObject = watch('object')
  const watchedValues = watch('values') as SearchFilterValue[]
  const watchedDropDownListFilters: GETDropDownFilter[] = watch(dropDownListFiltersInputName)
  const watchedDropDownWindowFilters: GETDropDownFilter[] = watch(dropDownWindowFiltersInputName)
  const watchedObjectValuePathArray = watch(objectValuePathArrayName)
  const watchedMin = watch(valueMinName)
  const watchedMax = watch(valueMaxName)
  const selectedObjectValue = watchedObjectValuePathArray?.[watchedObjectValuePathArray.length - 1]
  const selectedObjectValueType = selectedObjectValue?.valueType
  const watchFilterByObject = watch(filterByObjectInputName)

  const [initialPickValue, setInitialPickValue] = useState<SearchFilterValue>()
  const { data: object } = useFetchObjectByCodeQuery(watchedObject?.id, {
    skip: !watchedObject?.id,
  })
  // Используется для value options
  const objectFields = object?.fields

  useEffect(() => {
    if (!initialPickValue?.objectValue && !initialPickValue?.pathArray) {
      const clonedValue = structuredClone(watchedValue)
      setInitialPickValue(clonedValue)
    }

    return () => setInitialPickValue(undefined)
  }, [watchedValue?.objectValue])

  const objectField = useMemo(() => {
    if (watchedValuePathArray) {
      return { ...watchedValuePathArray[watchedValuePathArray.length - 1] }
    }

    return {}
  }, [watchedValuePathArray])

  const linkedObjectCode = objectField?.model ?? ''
  const isLinkedField = Boolean(objectField?.model) || watchFilterByObject

  const isGroupRelatedEnabled = useMemo(() => {
    return objectField?.isPk || isLinkedField
  }, [objectField, isLinkedField])

  const { data: dropdownEntities } = useFetchAllViewQuery(
    {
      objectCode: watchFilterByObject ? watchedObject?.id : linkedObjectCode,
      viewType: ENTITY_TYPES_REQUEST.DROP_DOWN_ENTITY,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: watchFilterByObject ? !watchedObject?.id : !linkedObjectCode,
    }
  )

  useEffect(() => {
    if (isDirty) {
      setValue(dropDownListCodeInputName, null)
      setValue(dropDownWindowInputName, null)
    }
  }, [watchedValueName])

  useEffect(() => {
    if (isDirty && !watchFilterByObject) {
      setValue(dropDownListCodeInputName, null)
      setValue(dropDownWindowInputName, null)
      setValue(objectValueInputName, null)
      setValue(fieldTypeInputName, null)
    }
  }, [watchFilterByObject])

  const { data: linkedObject } = useFetchObjectByCodeQuery(linkedObjectCode, {
    skip: !linkedObjectCode,
  })
  // Используется для objectValue options
  const linkedObjectFields = linkedObject?.fields

  useEffect(() => {
    if (isLinkedField) {
      setValue(linkedObjectCodeInputName, linkedObjectCode)
    }
  }, [linkedObjectCode, isLinkedField])

  // useEffect(() => {
  //   if (!isValueEmbedded) {
  //     setValue(objectValueInputName, null)
  //   }
  // }, [isValueEmbedded])

  useValueTypeOfSelectedValue({
    objectField,
    valueTypeInputName,
  })

  const isValueEmbedded = isObjectValueType(watchedValueType) || watchFilterByObject

  useResetGroupRelatedCheckboxOnValueNameChange({
    watchedValueName,
    isGroupRelatedEnabled,
    objectFields,
    objectField,
    isLinkedField,
    groupRelatedInputName,
  })

  useResetValuesOnValueNameChange({
    watchedValueName,
    objectValueInputName,
    fieldTypeInputName,
    formatDateInputName,
    objectValuePathArrayName,
  })

  useResetValuesOnObjectTitleChange({
    fieldTypeInputName,
    formatDateInputName,
    objectValueInputName,
    valueNameInputName,
    valueTypeInputName,
    objectValuePathArrayName,
  })

  useResetValuesOnGroupRelatedCheckboxChange({
    watchedGroupRelated,
    fieldTypeInputName,
    formatDateInputName,
    objectValueInputName,
    linkedObjectCodeInputName,
    defaultPlaceholderInputName,
    placeholderValueInputName,
    placeholderStartInputName,
    placeholderEndInputName,
    dropDownListCodeInputName,
    dropDownWindowInputName,
    dropDownListFiltersInputName,
    dropDownWindowFiltersInputName,
  })

  useResetAsCheckboxOnValueTypeChange({
    objectField,
    watchedAsCheckbox,
    watchedAsDuration,
    asCheckboxInputName,
    asDurationInputName,
    isDirty,
  })

  const {
    valueInput,
    valueTypeInput,
    asCheckboxInput,
    asDurationInput,
    formatDateInput,
    fieldTypeInput,
    defaultPlaceholderInput,
    groupRelatedInput,
    placeholderValueInput,
    placeholderStartInput,
    placeholderEndInput,
    dropDownListInput,
    dropDownWindowInput,
    timeIncrementInput,
    minInput,
    maxInput,
    timeUnitInput,
  } = useValueInputs({
    objectValueInputName,
    valueNameInputName,
    asCheckboxInputName,
    asDurationInputName,
    fieldTypeInputName,
    formatDateInputName,
    timeIncrementInputName,
    watchedValueName,
    watchedValue,
    watchedDefaultplaceholder,
    linkedObjectFields,
    objectFields,
    watchedObject,
    defaultPlaceholderInputName,
    groupRelatedInputName,
    placeholderValueInputName,
    placeholderStartInputName,
    placeholderEndInputName,
    dropDownListCodeInputName,
    dropDownWindowInputName,
    dropdownEntities: dropdownEntities?.data || [],
    valueTypeInputName,
    valueType: watchedValueType,
    addedFiltersByObject,
    valueMinName,
    valueMaxName,
    watchedMin,
    watchedMax,
    timeUnitName,
  })

  const showFieldTypeInput = useMemo(
    () =>
      (Boolean(watchedFieldType?.id) || watchedValueType === FIELD_VALUE_TYPE.DATETIME) &&
      !watchFilterByObject,
    [watchedFieldType, watchedValueType, watchFilterByObject]
  )

  const isObjectValueTypeDateTime = useMemo(
    () => selectedObjectValueType === FIELD_VALUE_TYPE.DATETIME,
    [selectedObjectValueType]
  )

  const showFormatDateInput = useMemo(
    () =>
      (Boolean(watchedFieldType?.id) && watchedFieldType?.id !== SEARCH_FIELD_TYPE.DAILY_RANGE) ||
      isObjectValueTypeDateTime,
    [watchedFieldType, isObjectValueTypeDateTime]
  )

  const showTimeIncrementInput = useMemo(
    () => Boolean(watchedFieldType?.id) && watchedFieldType.id === SEARCH_FIELD_TYPE.RELATIVE_VALUE,
    [watchedFieldType]
  )

  const showMinMaxInputs = useMemo(
    () => Boolean(watchedFieldType?.id) && watchedFieldType.id === SEARCH_FIELD_TYPE.RELATIVE_VALUE,
    [watchedFieldType]
  )

  const selectedValues = watchedValues.map(value => value.name?.id).filter(Boolean)

  const handleSubmitFilter = (filter: GETEntityFilter) => {
    if (currentTypeFilter) {
      const newFilter = { ...filter, type: currentTypeFilter }
      const isList = currentTypeFilter === DROPDOWN_FILTER_TYPES.LIST
      const inputName = isList ? dropDownListFiltersInputName : dropDownWindowFiltersInputName
      const updatedFilters = (
        isList ? watchedDropDownListFilters : watchedDropDownWindowFilters
      ).map((item, index) => (index === currentFilterPosition ? newFilter : item))

      if (currentFilterPosition !== null) {
        setValue(inputName, updatedFilters, {
          shouldDirty: true,
        })

        handleResetFilterDialog()

        return
      }

      setValue(
        inputName,
        [...(isList ? watchedDropDownListFilters : watchedDropDownWindowFilters), newFilter],
        {
          shouldDirty: true,
        }
      )
      handleResetFilterDialog()
    }
  }

  const handleDeleteFilter = (position: number, type: DROPDOWN_FILTER_TYPES) => {
    if (type === DROPDOWN_FILTER_TYPES.LIST) {
      setValue(
        dropDownListFiltersInputName,
        watchedDropDownListFilters.filter((_, index) => index !== position),
        { shouldDirty: true }
      )
    }
    if (type === DROPDOWN_FILTER_TYPES.WINDOW) {
      setValue(
        dropDownWindowFiltersInputName,
        watchedDropDownWindowFilters.filter((_, index) => index !== position),
        { shouldDirty: true }
      )
    }
  }

  const handleDoubleClickChip = (
    filter: GETEntityFilter,
    position: number,
    type: DROPDOWN_FILTER_TYPES
  ) => {
    setCurrentFilter(filter)
    setCurrentFilterPosition(position)
    setCurrentTypeFilter(type)
    setShowFilterDialog(true)
  }

  const handleResetFilterDialog = () => {
    setShowFilterDialog(false)
    setCurrentFilterPosition(null)
    setCurrentFilter(null)
    setCurrentTypeFilter(null)
  }

  const handleAddFilter = (type: DROPDOWN_FILTER_TYPES) => {
    setShowFilterDialog(true)
    setCurrentTypeFilter(type)
  }

  const handleCloseFilterDialog = (isDirty: boolean) => {
    if (isDirty && !confirm(t('notifications.leave'))) {
      return
    }

    handleResetFilterDialog()
  }

  const dropdownEntitiesInputs = (
    <Grid item flex={1}>
      <DropDownInputWithFitlers
        filters={watchedDropDownListFilters}
        inputs={[dropDownListInput]}
        watchInput={watchedDropDownListCode}
        onAddFilter={() => handleAddFilter(DROPDOWN_FILTER_TYPES.LIST)}
        onDeleteFilter={(_, index) => handleDeleteFilter(index, DROPDOWN_FILTER_TYPES.LIST)}
        onDoubleClickFilter={(filter, index) =>
          handleDoubleClickChip(filter, index, DROPDOWN_FILTER_TYPES.LIST)
        }
      />
      <DropDownInputWithFitlers
        filters={watchedDropDownWindowFilters}
        inputs={[dropDownWindowInput]}
        watchInput={watchedDropDownWindowCode}
        onAddFilter={() => handleAddFilter(DROPDOWN_FILTER_TYPES.WINDOW)}
        onDeleteFilter={(_, index) => handleDeleteFilter(index, DROPDOWN_FILTER_TYPES.WINDOW)}
        onDoubleClickFilter={(filter, index) =>
          handleDoubleClickChip(filter, index, DROPDOWN_FILTER_TYPES.WINDOW)
        }
      />
    </Grid>
  )

  const startEndPlaceholdersInputs = (
    <>
      <Grid item xs={5.5}>
        <FormInput {...placeholderStartInput} disableClearable />
      </Grid>
      <Grid item>
        <Divider sx={{ width: 10, mx: 0.5 }} />
      </Grid>
      <Grid item xs={4}>
        <FormInput {...placeholderEndInput} disableClearable label={undefined} />
      </Grid>
    </>
  )

  const placeholderInputs = (
    <Grid container item alignItems={'center'} xs={12}>
      {watchedFieldType?.label === SEARCH_FIELD_TYPE.RANGE_OF_VALUES ? (
        startEndPlaceholdersInputs
      ) : (
        <Grid item flexGrow={1}>
          <FormInput {...placeholderValueInput} disableClearable />
        </Grid>
      )}
      <Grid item>
        <FormInput {...defaultPlaceholderInput} disableClearable />
      </Grid>
    </Grid>
  )

  const generalInputs = (
    <>
      {showFieldTypeInput && <FormInput {...fieldTypeInput} disableClearable />}
      {showTimeIncrementInput && (
        <Grid container item spacing={2}>
          <Grid item xs={6}>
            <FormInput {...timeIncrementInput} disableClearable />
          </Grid>
          <Grid item xs={6}>
            <FormInput {...timeUnitInput} disableClearable />
          </Grid>
        </Grid>
      )}
      {showMinMaxInputs && (
        <Grid container item spacing={2}>
          <Grid item xs={6}>
            <FormInput {...minInput} disableClearable />
          </Grid>
          <Grid item xs={6}>
            <FormInput {...maxInput} disableClearable />
          </Grid>
        </Grid>
      )}
      {isValueEmbedded && (
        <Grid container item>
          <Grid>
            <Typography
              sx={{ fontSize: '1rem', minWidth: 120, mt: 1, color: theme.palette.color.label }}
            >
              {t('searchFilterForm.objectValue.label')}
            </Typography>
          </Grid>
          <Grid item flexGrow={1}>
            <ModalRefPathPicker
              hasClearInInput
              isRequired
              embeddedObjectPickerControllerName={objectValuePathArrayName}
              name={objectValueInputName}
              object={watchFilterByObject ? watchedObject : objectField.model}
              sxPickButton={{ m: 0, ml: 1 }}
            />
          </Grid>
        </Grid>
      )}
      {showFormatDateInput && <FormInput {...formatDateInput} disableClearable />}
      {/* Dropdown inputs */}
      {isLinkedField && dropdownEntitiesInputs}
    </>
  )

  const shouldDisplayAsCheckboxField =
    (watchedValueType === FIELD_VALUE_TYPE.INTEGER ||
      watchedValueType === FIELD_VALUE_TYPE.BOOLEAN) &&
    watchedType !== OBJECT_FIELD_TYPE.ENUM

  const filterValueInput = (
    <>
      <Grid container item>
        <Grid item flexGrow={1}>
          <ModalRefPathPicker
            hasClearInInput
            refPickAllowed
            embeddedObjectPickerControllerName={valuePathArrayName}
            isDisabled={!watchedObject?.id}
            isRequired={index === 0}
            label={t('searchFilterForm.value.label')}
            name={valueNameInputName}
            object={watchedObject}
            placeHolder={t('searchFilterForm.value.placeholder')}
            sxPickButton={{ m: 0, ml: 1 }}
          />
        </Grid>
        <Grid item>
          <FormInput
            inputType={GENERATOR_INPUT_TYPE.CHECKBOX}
            label={t('searchFilterForm.filterByObject.label')}
            labelPlacement='end'
            name={filterByObjectInputName}
          />
        </Grid>
        <Grid item>
          <FormInput {...groupRelatedInput} disableClearable disabled={!isGroupRelatedEnabled} />
        </Grid>
        {watchedValueName && (
          <Grid container item flexGrow={1}>
            <Grid item flexGrow={1}>
              <FormInput {...valueTypeInput} disableClearable />
            </Grid>
            {shouldDisplayAsCheckboxField && (
              <Grid item>
                <FormInput {...asCheckboxInput} disableClearable disabled={watchedAsDuration} />
              </Grid>
            )}
            {watchedValueType === FIELD_VALUE_TYPE.INTEGER && (
              <Grid item>
                <FormInput {...asDurationInput} disableClearable disabled={watchedAsCheckbox} />
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
    </>
  )

  return (
    <Grid container spacing={0} sx={{ mt: 0.5 }}>
      {index === 0 && (
        <Grid container item>
          <Divider sx={{ width: '100%', mb: 1 }} />
        </Grid>
      )}
      {isShowFilterDialog && (
        <FilterDialog
          filter={currentFilter}
          isShow={isShowFilterDialog}
          onClose={handleCloseFilterDialog}
          onSubmit={handleSubmitFilter}
        />
      )}
      {/* Filter value */}
      {filterValueInput}

      {/* General inputs */}
      {!watchedGroupRelated && generalInputs}
      {placeholderInputs}
      <Grid container item>
        <Divider sx={{ width: '100%' }} />
      </Grid>
    </Grid>
  )
}
